import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import { ContactDtoIn } from '@/models/in/ContactDtoIn';
import { GoalDtoIn } from '@/models/in/GoalDtoIn';
import GoalWithLimitsDtoIn from '@/models/in/GoalWithLimitsDtoIn';
import { SupportRequestFormData } from '../schemas/supportRequestFormSchema';
import { TileList, TileListItem } from '@/components/ui/Tile/Tile';
import IconsToGoals from '@/maps/IconsToGoals';
import wizardStyles from '@/pages/SupportRequests/NewSupportRequest.module.scss';

interface SupportGoalProps {
  selectedSupported: ContactDtoIn | null;
  isFundExpenseFlow?: boolean;
}

const EMPTY_LIMITS = {
  monthlyLimitFamily: null,
  monthlyLimitPerson: null,
  yearlyLimit: null,
  limitPercentage: null,
  personalLimit: null,
};

const SupportGoal = ({ selectedSupported, isFundExpenseFlow }: SupportGoalProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const { setValue, watch } = useFormContext<SupportRequestFormData>();
  const selectedGoalId = watch('goalWithLimits')?.goal?.id;

  const supportedGoalsUrl = currentFund?.id && selectedSupported?.id
    ? `funds/${currentFund.id}/supporteds/${selectedSupported.id}/available-goals`
    : '';

  const fundExpenseGoalsUrl = currentFund?.id && isFundExpenseFlow
    ? `funds/${currentFund.id}/fund-expense-goals`
    : '';

  const { data: supportedGoals, isLoading: isLoadingSupported, isError: isErrorSupported } =
    useGetAuthApi<GoalWithLimitsDtoIn[]>(supportedGoalsUrl);

  const { data: fundExpenseGoalsRaw, isLoading: isLoadingFund, isError: isErrorFund } =
    useGetAuthApi<GoalDtoIn[]>(fundExpenseGoalsUrl);

  const fundExpenseGoals = useMemo<GoalWithLimitsDtoIn[] | undefined>(() => {
    if (!fundExpenseGoalsRaw) return undefined;
    return fundExpenseGoalsRaw.map((goal) => ({ goal, limits: EMPTY_LIMITS }));
  }, [fundExpenseGoalsRaw]);

  const availableGoals = isFundExpenseFlow ? fundExpenseGoals : supportedGoals;
  const isLoading = isFundExpenseFlow ? isLoadingFund : isLoadingSupported;
  const isError = isFundExpenseFlow ? isErrorFund : isErrorSupported;

  const handleGoalToggle = (goalWithLimits: GoalWithLimitsDtoIn) => {
    if (selectedGoalId === goalWithLimits.goal.id) {
      setValue('goalWithLimits', null);
    } else {
      setValue('goalWithLimits', goalWithLimits);
      
      setValue('supportRecipient', null);
      setValue('childId', null);
      setValue('documents', []);
      setValue('additionalDocuments', []);
      setValue('multiRequestTotalCount', null);
    }
  };

  if (!isFundExpenseFlow && !selectedSupported) {
    return <p>{t('pleaseSelectSupportedFirst')}</p>;
  }

  const goalsTilesData: TileListItem[] = (availableGoals ?? []).map((goalWithLimits) => ({
    id: goalWithLimits.goal.id,
    label: t(goalWithLimits.goal.name),
    icon: IconsToGoals[goalWithLimits.goal.name] ?? undefined,
    subLabel: undefined,
    onClick: () => handleGoalToggle(goalWithLimits),
  }));

  if (isLoading) {
    return <p>{t('loadingGoals')}</p>;
  }

  if (isError) {
    return <p>{t('errorLoadingGoals')}</p>;
  }

  if (!availableGoals || availableGoals.length === 0) {
    if (isFundExpenseFlow) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>{t('noFundExpenseGoalsAvailable')}</p>
        </div>
      );
    }
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ marginBottom: '12px', fontSize: '16px' }}>
          {t('noGoalsAvailableReason')}
        </p>
        <p>
          {t('noGoalsAvailableAction')}
          <Link 
            to={currentFund?.id != null ? `/${currentFund.id}/support/supporteds` : '/'}
            state={{ supportedId: selectedSupported?.id }}
            style={{ color: 'var(--primary-color)', textDecoration: 'underline' }}
          >
            {t('supportedsPageLink')}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="p-[30px] max-md:p-4 ">
      {/* השאלה שפותחת את השלב במקור (.req-question). מוצגת במסך צר בלבד. */}
      <h2 className={wizardStyles.question}>{t('wizardGoalQuestion')}</h2>
      <TileList
        tiles={goalsTilesData}
        selectedId={selectedGoalId}
      />
    </div>
  );
};

export default SupportGoal;
